import type {
  DiscoverySummary,
  KnownPairSummary,
  PairRegistration,
  PrimerDiscovery,
  PrimerEstimate
} from '../../Model/OrderWizard/OrderWizardDraft.ts'
import type { DetectedSequencingPlatform } from '../../Util/sequencingPlatformDetection.ts'
import type { CanonicalPrimerLike, KnownPrimerPair } from '../../Util/orderSettingDetection/primerRegistry.ts'
import { findKnownPair, primersCompatible, stripAdapter } from '../../Util/orderSettingDetection/primerRegistry.ts'
import { cleanPrimer, toMasks } from '../../Util/orderSettingDetection/iupac.ts'
import type { SampleConfigurationGuess } from '../../Util/orderSettingDetection/sampleConfigurationClassifier.ts'
import type { LabPrimerHistory } from './estimationCore.ts'
import { historyEntries, isFailedEstimate } from './estimationCore.ts'

export type RegistryRow = CanonicalPrimerLike & { forwardName: string; reverseName: string }

const registrationsOf = (pair: KnownPrimerPair, rows: readonly RegistryRow[]): PairRegistration[] =>
  rows
    .filter((row) => findKnownPair({ forward: row.forwardSequence, reverse: row.reverseSequence }, [pair]) !== null)
    .map((row) => ({
      name: `${row.forwardName} / ${row.reverseName}`,
      forward: stripAdapter(row.forwardSequence),
      reverse: stripAdapter(row.reverseSequence)
    }))

/** Samples of the lab's earlier orders written with exactly this registration's two gene primers, in either field. */
const samplesWith = (registration: PairRegistration, history: LabPrimerHistory): number =>
  historyEntries(history).reduce((sum, entry) => {
    const [f, r] = [cleanPrimer(entry.forwardPrimer), cleanPrimer(entry.reversePrimer)]
    const { forward, reverse } = registration
    return (f.endsWith(forward) && r.endsWith(reverse)) || (f.endsWith(reverse) && r.endsWith(forward)) ? sum + entry.samples : sum
  }, 0)

/**
 * The registrations a pair folds together, the ones the lab used most first, then in registry order. The fold
 * joins genuinely different primers that agree at the 3′ end, so no one of them names the pair by itself.
 */
const rankedRegistrations = (pair: KnownPrimerPair, rows: readonly RegistryRow[], history: LabPrimerHistory = null) =>
  registrationsOf(pair, rows)
    .map((registration) => {
      const samples = samplesWith(registration, history)
      return samples > 0 ? { ...registration, samples } : registration
    })
    .sort((a, b) => (b.samples ?? 0) - (a.samples ?? 0))

/** Registry pairs fold several registrations together; name every one of them rather than inventing one name. */
export const namesForPair = (pair: KnownPrimerPair, rows: readonly RegistryRow[], history: LabPrimerHistory = null): string[] => [
  ...new Set(rankedRegistrations(pair, rows, history).map((registration) => registration.name))
]

/** The side of the pair the lab's earlier orders put in the forward field, by samples; none on a tie. */
const labForwardOf = (pair: KnownPrimerPair, history: LabPrimerHistory): KnownPairSummary['labForward'] => {
  let first = 0
  let second = 0
  for (const entry of historyEntries(history)) {
    const [f, r] = [stripAdapter(entry.forwardPrimer), stripAdapter(entry.reversePrimer)]
    if (primersCompatible(f, pair.first) && primersCompatible(r, pair.second)) {
      first += entry.samples
    } else if (primersCompatible(f, pair.second) && primersCompatible(r, pair.first)) {
      second += entry.samples
    }
  }
  if (first === second) {
    return undefined
  }
  return first > second ? 'first' : 'second'
}

const summarizePair = (pair: KnownPrimerPair, rows: readonly RegistryRow[], history: LabPrimerHistory): KnownPairSummary => {
  const registrations = rankedRegistrations(pair, rows, history)
  const labForward = labForwardOf(pair, history)
  return {
    first: pair.first,
    second: pair.second,
    amplicons: pair.amplicons,
    names: [...new Set(registrations.map((registration) => registration.name))],
    registrations,
    ...(labForward ? { labForward } : {}),
    firstVariants: pair.firstVariants,
    secondVariants: pair.secondVariants
  }
}

export type PairLabel = {
  /** The registration to call the pair by; `null` when the registry names none. */
  name: string | null
  /** Why that one: the samples' estimated primers, or the lab's earlier orders. */
  basis?: 'earlier-orders' | 'reads'
  /** The other registrations folded into the pair. */
  compatible: string[]
}

const sameSpellings = (registration: PairRegistration, a: string, b: string) =>
  (registration.forward === a && registration.reverse === b) || (registration.forward === b && registration.reverse === a)

/** `forward / reverse` as `reverse / forward`, for a pair written the other way round from its registration. */
const turned = (name: string) => {
  const parts = name.split(' / ')
  return parts.length === 2 ? `${parts[1]} / ${parts[0]}` : name
}

/**
 * What to call a found pair: the registration spelled like the gene primers estimated for most samples,
 * which is what gets written, its names in the order `estimates` put the primers in; without estimates,
 * the one the lab used most on earlier orders.
 */
export const pairLabel = (pair: KnownPairSummary, estimates: readonly (PrimerEstimate | undefined)[] = []): PairLabel => {
  const registrations: PairRegistration[] = pair.registrations ?? pair.names.map((name) => ({ name, forward: '', reverse: '' }))
  const tally = new Map<string, { a: string; b: string; count: number; byLab: number; aForward: number }>()
  for (const estimate of estimates) {
    const [a, b] = [estimate?.forward.gene, estimate?.reverse.gene]
    if (!estimate || isFailedEstimate(estimate) || !a || !b) {
      continue
    }
    const key = [a, b].sort().join('\u0000')
    const seen = tally.get(key) ?? { a, b, count: 0, byLab: 0, aForward: 0 }
    const byLab = estimate.forward.spelledByLab || estimate.reverse.spelledByLab
    tally.set(key, { ...seen, count: seen.count + 1, byLab: seen.byLab + (byLab ? 1 : 0), aForward: seen.aForward + (a === seen.a ? 1 : 0) })
  }
  const written = [...tally.values()].sort((x, y) => y.count - x.count)[0]
  const estimated = written && registrations.find((registration) => sameSpellings(registration, written.a, written.b))
  const chosen = estimated ?? registrations[0]
  const basis: PairLabel['basis'] = estimated
    ? written.byLab * 2 > written.count
      ? 'earlier-orders'
      : 'reads'
    : chosen?.samples
      ? 'earlier-orders'
      : undefined
  const forward = written && (written.aForward * 2 >= written.count ? written.a : written.b)
  const reversed = !!estimated && estimated.forward !== estimated.reverse && estimated.forward !== forward
  return {
    name: chosen ? (reversed ? turned(chosen.name) : chosen.name) : null,
    ...(basis ? { basis } : {}),
    compatible: [...new Set(registrations.map((registration) => registration.name))].filter((name) => name !== chosen?.name)
  }
}

type NamedPrimer = { side: 'forward' | 'reverse'; name: string; sequence: string }

/** log2 of how many concrete sequences the spelling stands for. */
const degeneracy = (sequence: string) => toMasks(sequence).reduce((sum, mask) => sum + Math.log2(bitCount(mask)), 0)
const bitCount = (mask: number) => [1, 2, 4, 8].filter((bit) => mask & bit).length || 4

/** Most registered first; among equals the more specific spelling. */
const rankedNames = (named: readonly NamedPrimer[]): string[] => {
  const byName = new Map<string, { count: number; degenerate: number }>()
  for (const { name, sequence } of named) {
    const seen = byName.get(name)
    byName.set(name, { count: (seen?.count ?? 0) + 1, degenerate: Math.min(seen?.degenerate ?? Infinity, degeneracy(sequence)) })
  }
  return [...byName].sort(([, a], [, b]) => b.count - a.count || a.degenerate - b.degenerate).map(([name]) => name)
}

/** The primer every candidate pair shares, named as the registry names it. */
const sharedPrimer = (
  candidates: readonly KnownPrimerPair[],
  rows: readonly RegistryRow[]
): { side: 'forward' | 'reverse'; names: string[] } | null => {
  const shared = [candidates[0].first, candidates[0].second].find((primer) =>
    candidates.every((pair) => primersCompatible(pair.first, primer) || primersCompatible(pair.second, primer))
  )
  if (!shared) {
    return null
  }
  const named = rows
    .filter((row) => candidates.some((pair) => findKnownPair({ forward: row.forwardSequence, reverse: row.reverseSequence }, [pair])))
    .flatMap((row): NamedPrimer[] => {
      const [forward, reverse] = [stripAdapter(row.forwardSequence), stripAdapter(row.reverseSequence)]
      return primersCompatible(forward, shared)
        ? [{ side: 'forward', name: row.forwardName, sequence: forward }]
        : primersCompatible(reverse, shared)
          ? [{ side: 'reverse', name: row.reverseName, sequence: reverse }]
          : []
    })
  const forward = named.filter((n) => n.side === 'forward').length
  return { side: forward * 2 >= named.length ? 'forward' : 'reverse', names: rankedNames(named) }
}

const partialDiscovery = (
  guess: SampleConfigurationGuess,
  rows: readonly RegistryRow[],
  unmated: DiscoveryContext['layoutUnmated'],
  history: LabPrimerHistory
): PrimerDiscovery => {
  const candidates = guess.oneSidedPairs
  const shared = sharedPrimer(candidates, rows)
  const side = shared?.side ?? 'forward'
  const hidden = side === 'forward' ? 'reverse' : 'forward'
  const why = unmated?.rawLength ? `${hidden} primer not visible in R${unmated.mate}-only reads` : `no ${hidden} primer at the reads' ends`
  return {
    status: 'partial',
    side,
    primerNames: shared?.names ?? [],
    candidates: candidates.map((pair) => summarizePair(pair, rows, history)),
    reason: candidates.length > 1 ? `${why}; ${candidates.length} registered pairs share the ${side} primer` : why
  }
}

export const primerDiscovery = (
  guess: SampleConfigurationGuess,
  rows: readonly RegistryRow[] | null,
  unmated: DiscoveryContext['layoutUnmated'],
  history: LabPrimerHistory = null
): PrimerDiscovery => {
  if (rows === null) {
    return { status: 'registry-unavailable', reason: 'the primer registry could not be loaded for this account' }
  }
  if (guess.pairsFound.length === 0 && guess.oneSidedPairs.length > 0) {
    return partialDiscovery(guess, rows, unmated, history)
  }
  if (guess.pairsFound.length === 0) {
    return { status: 'none', reason: 'no registered primer pair on the reads — already trimmed, or a primer the registry does not know' }
  }
  if (guess.pairsFound.length === 1) {
    return { status: 'found', pair: summarizePair(guess.pairsFound[0], rows, history), reason: 'found on the reads' }
  }
  return {
    status: 'mixed',
    pairs: guess.pairsFound.map((pair) => summarizePair(pair, rows, history)),
    reason: `${guess.pairsFound.length} different registered pairs across the scanned samples`
  }
}

/** A pair found on reads that showed one primer only before: R2 files joined them. */
export const settledByMates = (previous: PrimerDiscovery | undefined, next: PrimerDiscovery): PrimerDiscovery =>
  previous?.status === 'partial' && next.status === 'found'
    ? { ...next, reason: `${next.reason}, once the R2 files were there`, fromMates: true }
    : next

export type DiscoveryContext = {
  platform: DetectedSequencingPlatform
  /** `null` when the registry could not be loaded. */
  registryRows: readonly RegistryRow[] | null
  /** The lab's earlier primer entries, which rank the names of a pair; none when not loaded. */
  history?: LabPrimerHistory
  layoutReason: string
  layoutConfidence: DiscoverySummary['layout']['confidence']
  layoutPairs: number
  layoutUnmated?: DiscoverySummary['layout']['unmated']
  filesScanned: number
  runs?: DiscoverySummary['runs']
  readLengths?: DiscoverySummary['readLengths']
}

export const summarizeDiscovery = (guess: SampleConfigurationGuess, context: DiscoveryContext): DiscoverySummary => ({
  platform: context.platform,
  layout: {
    value: guess.layout,
    confidence: context.layoutConfidence,
    reason: context.layoutReason,
    pairs: context.layoutPairs,
    ...(context.layoutUnmated ? { unmated: context.layoutUnmated } : {})
  },
  primers: primerDiscovery(guess, context.registryRows, context.layoutUnmated, context.history ?? null),
  trimming: guess.trimming,
  multiplexing: guess.multiplexing,
  classifier: {
    configuration: guess.configuration,
    confidence: guess.confidence,
    reason: guess.reason,
    ...(guess.nearPairFiles ? { nearPairFiles: guess.nearPairFiles, judgedFiles: guess.judgedFiles } : {})
  },
  filesScanned: context.filesScanned,
  runs: context.runs,
  ...(context.readLengths ? { readLengths: context.readLengths } : {})
})
